import React, { useCallback, useEffect, useState } from 'react';
import { FileDetailModal, MoFile } from '../../components/FileDetailModal';

/**
 * 내 화면에 나올 영상 전부 — 간트 + 개별 제외.
 *
 * 내 슬롯셋의 예약(중첩 박스 포함)에 더해, 슬롯을 위임했다면 **조상이 편성한 목록**까지
 * 올려다본다. 위임한 슬롯에는 sync 미러로 찍힌 한 장면만 보여서, 앞으로 뭐가 나올지는
 * 내 슬롯셋만 봐서는 알 수 없기 때문이다.
 *
 * 막대 표기 규칙(범례와 일치시킬 것):
 *   실선      연속 노출
 *   점선      sequence 박스 — 순번이 올 때만(등장 시점은 커서라 시간으로 예측 불가)
 *   흐림      schedule 에서 다른 항목에 가려짐 — 기간은 있으나 **재생되지 않는다**
 *   회색      박스 기본 영상(예약이 빈 구간을 채움)
 *   취소선    이 화면에서 제외됨
 */
interface Row {
  slotNumber: number | null;
  boxId: number | null;
  entryId: number | null;
  fileHash: string;
  start: number;
  end: number;
  rotating: boolean;
  shadowed: boolean;
  isDefault: boolean;
  always: boolean;
  depth: number;
  title: string | null;
  thumbnailPath: string | null;
  filtered: boolean;
  currentlyVisible: boolean;
}

interface Section {
  kind: 'self' | 'ancestor' | 'supply' | 'filtered_only';
  familyHash: string | null;
  label: string;
  rows: Row[];
}

interface ScreenContent {
  setName: string | null;
  windowStart: number;
  windowEnd: number;
  sections: Section[];
  canFilter: boolean;
}

const D = 86_400_000;
const DOW = ['일', '월', '화', '수', '목', '금', '토'];
const dayInfo = (t: number) => {
  const d = new Date(t);
  return { md: `${d.getMonth() + 1}/${d.getDate()}`, dow: DOW[d.getDay()], weekday: d.getDay() };
};

export const ScheduleTab: React.FC<{ familyHash: string }> = ({ familyHash }) => {
  const [data, setData] = useState<ScreenContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // 영상 상세 — 영상 목록 페이지와 **같은 모달**을 띄운다(별도 모달 만들지 않음).
  const [selectedFile, setSelectedFile] = useState<MoFile | null>(null);
  // 조상(공급) 섹션은 기본 접힘 — 헤더 클릭으로 펼침. 키 = familyHash 또는 라벨.
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const toggleSection = (k: string) => setOpenSections(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const load = useCallback((signal?: { cancelled: boolean }) => {
    return fetch(`/videobox/screen-content/${familyHash}`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => {
        if (signal?.cancelled) return;
        if (!j.success) { setError(j.error || '불러오지 못했습니다.'); return; }
        setData(j.data);
        setError(null);
      })
      .catch(() => { if (!signal?.cancelled) setError('불러오지 못했습니다.'); });
  }, [familyHash]);

  useEffect(() => {
    const signal = { cancelled: false };
    setLoading(true);
    load(signal).finally(() => { if (!signal.cancelled) setLoading(false); });
    return () => { signal.cancelled = true; };
  }, [load]);

  /**
   * 영상 상세 열기 — `/mofile/:hash/info` 로 채워 **영상 목록과 같은 모달**을 띄운다.
   * MoFile.tsx 의 달력 클릭(openEventFile)과 같은 경로다.
   *
   * ⚠️ 조상 섹션 영상은 **남의 소유**다. /info 는 "활성 슬롯에 참조된 파일이면 소유자가
   *    아니어도 메타데이터 허용" 이라 열리고(그 파일 해시는 이미 화면에 보인다),
   *    삭제·수정은 서버가 uploaded_by 로 403 낸다.
   */
  const openFileDetail = useCallback(async (row: Row) => {
    try {
      const r = await fetch(`/mofile/${row.fileHash}/info`, { credentials: 'include' });
      const body = r.ok ? await r.json() : null;
      if (!body?.success || !body.data) { setNotice('이 영상의 정보를 불러오지 못했습니다.'); return; }
      const d = body.data;
      setSelectedFile({
        file_hash: d.file_hash,
        title: d.title ?? row.title ?? '',
        file_type: d.file_type ?? undefined,
        duration: d.duration ?? null,
        thumbnail_path: d.thumbnail_path ?? row.thumbnailPath ?? undefined,
        event_start_at: d.event_start_at ?? null,
        expires_at: d.expires_at ?? null,
        publish_at: d.publish_at ?? null,
        event_label: d.event_label ?? null,
        overlay_position: d.overlay_position ?? null,
        // /info 는 업로드 일자를 주지 않는다 — 그럴듯한 값으로 채우지 않고 비워 둔다.
        created_at: '',
        file_url: `/mofile/download/${d.file_hash}`,
        can_edit: d.can_edit === true,
      });
    } catch {
      setNotice('이 영상의 정보를 불러오지 못했습니다.');
    }
  }, []);

  /** 제외 토글 — 서버가 소유자 여부를 다시 확인하므로 여기 게이팅은 편의일 뿐이다. */
  const toggleFilter = useCallback(async (row: Row) => {
    setBusy(row.fileHash);
    setNotice(null);
    try {
      const res = row.filtered
        ? await fetch(`/videobox/filters/${familyHash}/${row.fileHash}`, {
            method: 'DELETE', credentials: 'include',
          })
        : await fetch(`/videobox/filters/${familyHash}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileHash: row.fileHash }),
          });
      const j = await res.json();
      if (!j.success) { setNotice(j.error || '변경하지 못했습니다.'); return; }
      await load();
    } catch {
      setNotice('변경하지 못했습니다.');
    } finally {
      setBusy(null);
    }
  }, [familyHash, load]);

  if (loading) return <p className="text-sm text-gray-500 py-8 text-center">불러오는 중...</p>;
  if (error) return <p className="text-sm text-red-500 py-8 text-center">{error}</p>;

  const totalRows = data ? data.sections.reduce((n, s) => n + s.rows.length, 0) : 0;
  if (!data || totalRows === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">이 화면에 나올 영상이 없습니다.</p>
        <p className="text-xs text-gray-400 mt-1">슬롯에 영상을 직접 넣거나, 영상 박스를 슬롯에 연결하세요.</p>
      </div>
    );
  }

  // 날짜 컬럼 — 창을 일 단위로 자른다(로컬 자정 기준).
  const startDay = new Date(data.windowStart); startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(data.windowEnd); endDay.setHours(0, 0, 0, 0);
  const dayCount = Math.max(1, Math.round((endDay.getTime() - startDay.getTime()) / D) + 1);
  const axisStart = startDay.getTime();
  const axisEnd = axisStart + dayCount * D;
  const span = axisEnd - axisStart;

  const pct = (t: number) => ((Math.min(Math.max(t, axisStart), axisEnd) - axisStart) / span) * 100;

  // 오늘이 축의 몇 번째 칸인가. 창은 지금부터 시작하므로 보통 0 이지만, 창이 과거를
  // 담게 되면 자연히 따라간다(축 밖이면 어느 칸과도 안 맞아 강조가 안 붙는다).
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayIndex = Math.round((todayStart.getTime() - axisStart) / D);
  // 칸이 좁으면 날짜 라벨이 서로 겹친다 — 축 전체에 12개 안쪽으로만 찍는다.
  const labelEvery = Math.max(1, Math.ceil(dayCount / 12));

  /** 한 행(라벨 + 막대). 슬롯 헤더는 호출부가 붙인다. */
  const now = Date.now();
  const renderRow = (r: Row, key: string) => (
    <div key={key} className="flex border-b border-gray-100 hover:bg-blue-50/40">
      {/* 들여쓰기는 컨테이너에 준다 — 썸네일까지 함께 밀려야 중첩 박스 깊이가 읽힌다. */}
      <div
        className="w-72 shrink-0 pr-2 py-1.5 text-xs flex items-center gap-1.5 min-w-0"
        style={{ paddingLeft: `${8 + r.depth * 12}px` }}
      >
        <button
          type="button"
          onClick={() => openFileDetail(r)}
          title="클릭하여 영상 상세 보기"
          className="relative w-11 h-6 shrink-0 rounded bg-gray-100 overflow-hidden flex items-center justify-center group"
        >
          {r.thumbnailPath ? (
            <img
              src={`/mofile/thumbnail/${r.thumbnailPath}`}
              alt=""
              loading="lazy"
              className={[
                'w-full h-full object-cover',
                r.filtered ? 'opacity-30 grayscale' : r.shadowed ? 'opacity-40' : '',
              ].join(' ')}
            />
          ) : (
            <span className="text-[9px] text-gray-400">없음</span>
          )}
          <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs">▶</span>
        </button>
        <button
          type="button"
          onClick={() => openFileDetail(r)}
          title={r.title || r.fileHash}
          className={[
            'truncate text-left hover:text-blue-600',
            r.filtered ? 'line-through text-gray-400' : r.shadowed ? 'text-gray-400' : 'text-gray-800',
          ].join(' ')}
        >
          {r.title || '(제목 없음)'}
        </button>
        {r.isDefault && <span className="text-[10px] text-gray-400 shrink-0">기본</span>}
        {r.always && !r.filtered && <span className="text-[10px] text-green-600 shrink-0">상시</span>}
        {!r.currentlyVisible && !r.filtered && (
          // 시퀀스 항목이 창 안에 있고 가려지지 않았으면 "지금 안 나옴"이 아니라
          // "돌아가며 나옴"이 정확하다. 창 밖/가려짐은 종전 문구 유지.
          r.rotating && !r.shadowed && r.start <= now && now < r.end ? (
            <span className="text-[10px] text-blue-500 shrink-0" title="시퀀스 — 자기 차례마다 번갈아 노출됩니다">
              순차 노출
            </span>
          ) : (
            <span className="text-[10px] text-gray-400 shrink-0" title="지금 화면에 나오고 있지 않습니다">
              현재 미노출
            </span>
          )
        )}
        {data.canFilter && (
          <button
            type="button"
            onClick={() => toggleFilter(r)}
            disabled={busy === r.fileHash}
            className={[
              'ml-auto shrink-0 px-1.5 py-0.5 rounded text-[10px] border transition-colors',
              busy === r.fileHash ? 'opacity-50 cursor-wait' : '',
              r.filtered
                ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                : 'border-red-200 text-red-600 hover:bg-red-50',
            ].join(' ')}
          >
            {r.filtered ? '다시 받기' : '안 받기'}
          </button>
        )}
      </div>
      <div className="flex-1 relative h-7">
        {/* 날짜 격자 — 오늘 칸은 옅게 칠해 세로로 훑을 때 기준선이 되게 한다. */}
        {Array.from({ length: dayCount }, (_, d) => (
          <div
            key={d}
            className={[
              'absolute top-0 bottom-0 border-l',
              d === todayIndex ? 'border-blue-300 bg-blue-100/40' : 'border-gray-100',
            ].join(' ')}
            style={{
              left: `${(d / dayCount) * 100}%`,
              width: d === todayIndex ? `${(1 / dayCount) * 100}%` : undefined,
            }}
          />
        ))}
        <div
          className={[
            'absolute top-1.5 h-4 rounded-sm',
            r.isDefault ? 'bg-gray-400' : 'bg-blue-500',
            r.shadowed ? 'opacity-25' : '',
            r.filtered ? 'opacity-20 grayscale' : '',
            r.rotating ? 'bg-transparent border-2 border-dashed' : '',
            r.rotating ? (r.isDefault ? 'border-gray-400' : 'border-blue-500') : '',
          ].join(' ')}
          style={{ left: `${pct(r.start)}%`, width: `${Math.max(pct(r.end) - pct(r.start), 0.5)}%` }}
          title={[
            r.title || r.fileHash.slice(0, 16),
            `${new Date(r.start).toLocaleString('ko-KR')} ~ ${new Date(r.end).toLocaleString('ko-KR')}`,
            r.filtered ? '✕ 이 화면에서 제외됨 — 재생되지 않습니다' : '',
            r.shadowed ? '⚠ 다른 예약에 가려져 재생되지 않습니다' : '',
            r.rotating ? '시퀀스 — 순번이 올 때만 재생' : '',
            r.isDefault ? '박스 기본 영상' : '',
            r.always ? '슬롯에 직접 배정 — 상시 재생' : '',
          ].filter(Boolean).join('\n')}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">
          내 화면에 나올 영상
          {data.setName && <span className="text-gray-400 font-normal"> (슬롯셋 {data.setName})</span>}
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-6 h-2 bg-blue-500 rounded-sm" />연속</span>
          <span className="flex items-center gap-1"><span className="w-6 h-2 rounded-sm border-2 border-dashed border-blue-500" />순번(시퀀스)</span>
          <span className="flex items-center gap-1"><span className="w-6 h-2 bg-blue-500 rounded-sm opacity-25" />가려짐(재생 안 됨)</span>
          <span className="flex items-center gap-1"><span className="w-6 h-2 bg-gray-400 rounded-sm" />기본 영상</span>
          <span className="flex items-center gap-1"><span className="w-6 h-2 bg-blue-500 rounded-sm opacity-20 grayscale" />제외됨</span>
        </div>
      </div>

      {notice && <p className="text-xs text-red-500">{notice}</p>}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <div className="min-w-[780px]">
          {/* 날짜 헤더 */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <div className="w-72 shrink-0 px-2 py-1.5 text-xs font-medium text-gray-500">영상</div>
            {/*
              격자와 라벨을 **분리한다.** 한 칸 폭에 라벨을 넣으면, 창이 길어질수록
              칸이 1~2px 로 좁아져 overflow-hidden 에 글자가 통째로 잘린다(기존 결함).
              격자·오늘칠은 칸 폭이 필요하고, 라벨은 칸 폭에 매일 이유가 없다.
            */}
            <div className="flex-1 relative h-9 overflow-hidden">
              {Array.from({ length: dayCount }, (_, i) => (
                <div
                  key={`g${i}`}
                  className={[
                    'absolute top-0 bottom-0 border-l',
                    i === todayIndex ? 'border-blue-400 bg-blue-100/70' : 'border-gray-200',
                  ].join(' ')}
                  style={{
                    left: `${(i / dayCount) * 100}%`,
                    width: i === todayIndex ? `${(1 / dayCount) * 100}%` : undefined,
                  }}
                />
              ))}
              {Array.from({ length: dayCount }, (_, i) => {
                // 라벨은 축 전체에 12개 안쪽으로만. 오늘은 밀도와 무관하게 항상 찍는다.
                if (i !== todayIndex && i % labelEvery !== 0) return null;
                const { md, dow, weekday } = dayInfo(axisStart + i * D);
                const isToday = i === todayIndex;
                return (
                  <div
                    key={`l${i}`}
                    className="absolute top-0 text-[10px] pl-1 pt-1 leading-tight whitespace-nowrap"
                    style={{ left: `${(i / dayCount) * 100}%` }}
                    title={`${md} (${dow})${isToday ? ' — 오늘' : ''}`}
                  >
                    <div className={isToday ? 'font-semibold text-blue-700' : 'text-gray-400'}>{md}</div>
                    <div
                      className={
                        isToday ? 'font-semibold text-blue-700'
                          : weekday === 0 ? 'text-red-400'
                          : weekday === 6 ? 'text-blue-400'
                          : 'text-gray-400'
                      }
                    >
                      {isToday ? `오늘 ${dow}` : dow}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(() => {
            // supply 섹션(슬롯별 공급 예정)은 하단이 아니라 self 의 해당 슬롯 아래에 붙인다.
            const supplyBySlot = new Map<number, Section[]>();
            for (const sec of data.sections) {
              if (sec.kind !== 'supply') continue;
              const n = sec.rows[0]?.slotNumber ?? -1;
              const list = supplyBySlot.get(n) ?? [];
              list.push(sec);
              supplyBySlot.set(n, list);
            }
            return data.sections.filter(sec => sec.kind !== 'supply').map((section, si) => {
            // self 섹션은 슬롯이 화면의 자리이므로 슬롯별로 묶는다.
            // 조상 섹션은 내 슬롯 번호가 아니라 slotNumber 가 없다 — 평평하게 나열한다.
            const grouped = section.kind === 'self';
            const bySlot = new Map<number, Row[]>();
            if (grouped) {
              for (const r of section.rows) {
                const n = r.slotNumber ?? -1;
                const list = bySlot.get(n) ?? [];
                list.push(r);
                bySlot.set(n, list);
              }
            }
            // 조상(공급) 섹션은 기본 접힘 — self 의 "지금" 과 달리 "앞으로" 정보라 보조적.
            const sectionKey = section.familyHash ?? `${section.kind}-${section.label}`;
            const collapsible = section.kind === 'ancestor';
            const isOpen = !collapsible || openSections.has(sectionKey);
            return (
              <div key={`${section.kind}-${section.familyHash ?? si}`}>
                {section.kind !== 'self' && (
                  <div
                    className={`px-2 py-1 bg-amber-50/70 border-y border-amber-100 text-[11px] font-medium text-amber-800 ${collapsible ? 'cursor-pointer hover:bg-amber-100/70 select-none' : ''}`}
                    onClick={collapsible ? () => toggleSection(sectionKey) : undefined}
                    title={collapsible ? (isOpen ? '접기' : '펼치기') : undefined}
                  >
                    {collapsible && <span className="mr-1 font-mono">{isOpen ? '▾' : '▸'}</span>}
                    {section.label}
                    {section.kind === 'ancestor' && (
                      <span className="ml-1 font-normal text-amber-600">
                        — 위임한 슬롯에 이쪽 편성이 나옵니다{!isOpen && ` (${section.rows.length}건)`}
                      </span>
                    )}
                  </div>
                )}
                {isOpen && (grouped
                  ? [...new Set([...bySlot.keys(), ...supplyBySlot.keys()])].sort((a, b) => a - b).map(slotNumber => (
                      <div key={slotNumber}>
                        <div className="px-2 py-1 bg-gray-50/60 border-b border-gray-100 text-[11px] font-medium text-gray-500">
                          슬롯 {slotNumber}
                        </div>
                        {(bySlot.get(slotNumber) ?? []).map((r, i) =>
                          renderRow(r, `${r.boxId}-${r.entryId}-${r.start}-${i}`),
                        )}
                        {(supplyBySlot.get(slotNumber) ?? []).map((sup, supIdx) => {
                          const k = `supply-${slotNumber}-${sup.familyHash ?? supIdx}`;
                          const open = openSections.has(k);
                          return (
                            <div key={k}>
                              <div
                                className="px-2 py-1 bg-amber-50/70 border-y border-amber-100 text-[11px] text-amber-800 cursor-pointer hover:bg-amber-100/70 select-none"
                                style={{ paddingLeft: '20px' }}
                                onClick={() => toggleSection(k)}
                                title={open ? '접기' : '펼치기'}
                              >
                                <span className="mr-1 font-mono">{open ? '▾' : '▸'}</span>
                                {sup.label}
                                <span className="ml-1 font-normal text-amber-600">
                                  — 이 슬롯에 앞으로 나올 편성{!open && ` (${sup.rows.length}건)`}
                                </span>
                              </div>
                              {open && sup.rows.map((r, i) =>
                                renderRow(r, `${k}-${r.fileHash}-${r.start}-${i}`),
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  : section.rows.map((r, i) =>
                      renderRow(r, `${section.kind}-${si}-${r.fileHash}-${r.start}-${i}`),
                    ))}
              </div>
            );
          });
          })()}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        제외는 <strong>이 모패밀리 화면에만</strong> 적용됩니다 — 상대의 편성이나 위임은 바뀌지 않고,
        상대에게 알림도 가지 않습니다. 제외된 차례는 다음 영상으로 넘어갑니다.
        가려진(흐린) 막대는 기간이 잡혀 있어도 실제로는 재생되지 않습니다.
      </p>

      {/*
        영상 목록 페이지와 **같은 모달**. 삭제·제목/행사 수정은 모달이 스스로 수행하고
        콜백은 "부모는 다시 읽어라" 신호일 뿐이라, 여기서 할 일은 목록 갱신뿐이다.
      */}
      <FileDetailModal
        file={selectedFile}
        // 남의 영상(조상 섹션)이면 변경 수단을 감춘다 — 판단은 서버가 한다(can_edit).
        readOnly={selectedFile?.can_edit === false}
        onClose={() => setSelectedFile(null)}
        onDelete={() => { setSelectedFile(null); void load(); }}
        onUpdate={() => { void load(); }}
      />
    </div>
  );
};

export default ScheduleTab;
